<template>
  <div class="client-page mandat-page">
    <header class="mandat-page__hero client-page__header">
      <h1 class="client-page__title mandat-page__title">Mes recommandations</h1>
      <p class="client-page__intro mandat-page__subtitle">
        Suivez les actions convenues avec votre conseiller et mettez à jour leur avancement.
      </p>
    </header>

    <div class="mandat-page__toolbar">
      <div class="mandat-page__search">
        <span class="mandat-page__search-icon" aria-hidden="true">
          <i class="fas fa-search"></i>
        </span>
        <input type="search" class="form-control" placeholder="Rechercher…" v-model="searchQuery" autocomplete="off">
      </div>
      <button
        v-if="recommendations.length"
        type="button"
        class="btn btn-primary"
        @click="exportToExcel"
      >
        <i class="fas fa-file-csv me-2"></i>
        Exporter (CSV)
      </button>
    </div>

    <div class="mandat-table-wrap">
      <div v-if="errorMessage" class="alert alert-danger mandat-alert">{{ errorMessage }}</div>
      <div v-if="loading" class="mandat-loading text-center">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Chargement…</span>
        </div>
      </div>
      <div v-else-if="recommendations.length === 0" class="alert alert-info mandat-empty">
        Aucune recommandation pour le moment.
      </div>
      <div v-else class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th>Titre</th>
              <th>Date de fin</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in paginatedItems" :key="item.id">
              <td>{{ item.titre }}</td>
              <td>{{ formatDate(item.date_echeance) }}</td>
              <td>
                <span :class="getStatusClass(item.statut)">{{ formatStatut(item.statut) }}</span>
              </td>
              <td>
                <button type="button" class="mandat-icon-btn mandat-icon-btn--edit" @click="editItem(item)" title="Mettre à jour le statut">
                  <i class="fas fa-edit"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="mandat-pagination">
          <div class="mandat-pagination__size">
            <span>Afficher</span>
            <select v-model="itemsPerPage" class="form-select form-select-sm">
              <option v-for="n in pageSizes" :key="n" :value="n">{{ n }}</option>
            </select>
            <span>éléments</span>
          </div>
          <div class="mandat-pagination__nav">
            <span>
              {{ (currentPage - 1) * itemsPerPage + 1 }}–{{ Math.min(currentPage * itemsPerPage, filteredItems.length) }}
              sur {{ filteredItems.length }}
            </span>
            <div class="btn-group">
              <button type="button" class="btn btn-outline-secondary btn-sm" @click="currentPage--" :disabled="currentPage === 1">
                <i class="fas fa-chevron-left"></i>
              </button>
              <button type="button" class="btn btn-outline-secondary btn-sm" @click="currentPage++" :disabled="currentPage >= totalPages">
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import { isAuthenticatedSession } from '@/utils/sessionAuth';
import {
  formatMandatDate,
  formatMandatStatut,
  mandatStatutClass,
  MANDAT_PAGE_SIZES,
} from '@/utils/mandatStatus';


const TOKEN_KEYS = {
  ACCESS: 'auth_session',
  REFRESH: null /* httpOnly cookie */,
  ROLE: 'user_role'
};

// const statusOptions = {
//   'en_cours': 'En cours',
//   'terminee': 'Terminé',
// };

export default {
  data() {
    return {
      recommendations: [],
      searchQuery: '',
      sortField: 'date_creation',
      sortDirection: 'desc',
      errorMessage: '',
      loading: false,
      currentPage: 1,
      itemsPerPage: 10,
      pageSizes: MANDAT_PAGE_SIZES,
      statusOptions: {
      'en_cours': 'En cours',
      'terminee': 'Terminé',
    },
    }
  },
  computed: {
    filteredItems() {
      const searchLower = this.searchQuery.toLowerCase();
      let items = this.recommendations.filter(item => {
        const titre = (item.titre || '').toLowerCase();
        const desc = (item.description || '').toLowerCase();
        const statLabel = (item.get_statut_display || item.statut || '').toString().toLowerCase();
        return (
          titre.includes(searchLower) ||
          desc.includes(searchLower) ||
          statLabel.includes(searchLower)
        );
      });

      items.sort((a, b) => {
        const modifier = this.sortDirection === 'desc' ? -1 : 1;
        if (this.sortField === 'date_creation' || this.sortField === 'date_echeance') {
          const da = new Date(a[this.sortField] || 0).getTime();
          const db = new Date(b[this.sortField] || 0).getTime();
          return (da - db) * modifier;
        }
        return String(a[this.sortField] || '').localeCompare(String(b[this.sortField] || '')) * modifier;
      });

      return items;
    },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredItems.slice(start, start + this.itemsPerPage);
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredItems.length / this.itemsPerPage));
    }
  },
  methods: {
    async fetchRecommendations() {
      this.loading = true; // Activer l'indicateur de chargement
      this.errorMessage = ''; // Réinitialiser le message d'erreur

      try {
        if (!isAuthenticatedSession()) {
          this.errorMessage = "Session expirée. Veuillez vous reconnecter.";
          return;
        }
        const response = await axios.get('/api/actions-recommandations/', {
          withCredentials: true
        });

        // Assurez-vous que la réponse contient un tableau de recommandations
        this.recommendations = response.data.recommandations || [];
        this.errorMessage = '';
      } catch (error) {
        console.error('Erreur lors du chargement des recommandations:', error);
        if (error.response && error.response.status === 401) {
          this.errorMessage = 'Votre session a expiré. Veuillez vous reconnecter.';
          this.$router.push('/connexion');
        } else {
          this.errorMessage = "Une erreur est survenue lors du chargement des recommandations. Veuillez réessayer.";
        }
      } finally {
        this.loading = false; // Désactiver l'indicateur de chargement
      }
    },
    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'asc';
      }
      this.currentPage = 1;
    },
    sortIcon(field) {
      if (this.sortField !== field) return 'fa-sort text-muted';
      return this.sortDirection === 'asc' ? 'fa-sort-up text-primary' : 'fa-sort-down text-primary';
    },
    formatDate(date) {
      return formatMandatDate(date, { long: true });
    },
    formatStatut(statut) {
      return formatMandatStatut(statut);
    },
    getStatusClass(statut) {
      return mandatStatutClass(statut);
    },
    getStatusBadgeClass(statut) {
      const classes = {
        'non_commencee': 'bg-secondary',
        'en_cours': 'bg-primary',
        'terminee': 'bg-success',
        'en_attente': 'bg-warning',
        'annulee': 'bg-danger'
      };
      return classes[statut] || '';
    },

    async editItem(item) {
      try {
        if (!isAuthenticatedSession()) throw new Error('Session expirée');

        // S'assurer qu'il y a un RACI client associé à l'item de recommandation
        if (!item.raci_client || item.raci_client.length === 0) {
            Swal.fire('Erreur', 'Aucune action client associée pour mettre à jour le statut.', 'error');
            return; // Sortir de la fonction si pas de RACI client
        }

        // Utiliser l'ID du RACI client
        const raciClientId = item.raci_client[0].id;
        const currentStatus = item.raci_client[0].statut; // Utiliser le statut du RACI client


        const { value: newStatus } = await Swal.fire({
          title: 'Modifier le statut',
          input: 'select',
          inputOptions: {
            'en_cours': 'En cours',
            'terminee': 'Terminée',
          },
          inputValue: currentStatus, // Pré-sélectionner le statut actuel du RACI
          showCancelButton: true
        });

        if (newStatus) {
          Swal.showLoading();

          try {
            const { data } = await axios.patch(
              // *** CORRECTION ICI : Utiliser l'URL de la vue RACIClientUpdateStatutView ***
              `/api/raci-client/${raciClientId}/statut/`,
              { statut: newStatus },
              {
                withCredentials: true
              }
            );

            // Mise à jour réactive (directement l'objet RACI client)
            item.raci_client[0].statut = newStatus;
            // Si tu veux aussi que le statut de la recommandation mère se mette à jour,
            // tu peux ajouter une logique ici, mais ce n'est pas géré automatiquement
            // par l'endpoint RACIClientUpdateStatutView. L'endpoint de la recommandation
            // le faisait synchrone, mais causait l'erreur.
            // item.statut = newStatus; // Optionnel, dépend de ta logique métier

            Swal.fire({
              title: 'Succès!',
              // Afficher le statut affichable retourné par le backend RACIClient
              text: `Statut : ${data.statut_display}`,
              icon: 'success',
              timer: 1500
            });

          } catch (error) {
             console.error('Erreur lors de la mise à jour de l\'action client:', error);
             if (error.response?.status === 401) {
                 this.errorMessage = 'Votre session a expiré. Veuillez vous reconnecter.';
                 this.$router.push('/connexion');
                 return;
             }
             let msg = error.response?.data?.error || error.message;
             if (error.response?.status === 400 && typeof error.response.data === 'object') {
                 // Si la réponse 400 est un objet (les erreurs du serializer par ex)
                 msg = Object.values(error.response.data).flat().join(' ');
             }
             Swal.fire('Erreur', `Échec de la mise à jour : ${msg}`, 'error');
          } finally {
            Swal.hideLoading();
          }
        }
      } catch (error) {
        console.error('Erreur editItem:', error);
        if (error.message === 'Session expirée') {
            console.error("Session expirée. Veuillez vous reconnecter.");
            return; // Retourner pour éviter l'affichage Swal
        }
        Swal.fire('Erreur', error.message, 'error');
      }
    },
    exportToExcel() {
      const rows = this.filteredItems;
      if (!rows.length) {
        Swal.fire('Export', 'Aucune recommandation à exporter.', 'info');
        return;
      }
      const escapeCell = (value) => {
        const s = String(value ?? '').replace(/"/g, '""');
        return `"${s}"`;
      };
      const header = ['Titre', 'Description', 'Date de fin', 'Statut'].map(escapeCell).join(';');
      const body = rows.map((item) => [
        item.titre,
        item.description,
        this.formatDate(item.date_echeance),
        this.formatStatut(item.statut),
      ].map(escapeCell).join(';'));
      const csv = `\ufeff${header}\r\n${body.join('\r\n')}`;
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `recommandations_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  },
  mounted() {
    this.fetchRecommendations();
  },
  watch: {
    itemsPerPage() {
      this.currentPage = 1;
    },
    searchQuery() {
      this.currentPage = 1;
    }
  }
}
</script>

<style scoped>
.table {
  min-width: 560px;
}
</style>
